import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal, notification } from 'antd';
import { SiOkta } from "react-icons/si";
import { database, storage, storageRef } from 'utils/firebase';
import { ref, update, get, child } from 'firebase/database';
import { getDownloadURL, uploadBytesResumable } from 'firebase/storage';
import { setTranscriptionState, setMeetMessageId, setStopKey, setForceStop, setStopTranscription, setMeetButtonClicked } from 'slices/transcription.slice';
import { soapNoteAIMeeting, customCloudMessage } from 'MentalHealth/pages/ActiveChats/apis';
import { useLocation } from 'react-router-dom';
import styles from './TranscriptionManager.module.scss';
import { current } from '@reduxjs/toolkit';

const TranscriptionManager = () => {
    console.log("TranscriptionManager rendered");
    //revert back
    const dispatch = useDispatch();
    const location = useLocation();
    const { isTranscribing, meetmsgID, meetButtonClicked, stopKey, shouldStopTranscription, forceStop } = useSelector(state => state.transcription);
    const { currentSession } = useSelector(state => state.mentalHealth);
    const { me: doctorProfile } = useSelector(state => state.app);

    // Local state for live transcript
    const [liveTranscript, setLiveTranscript] = React.useState("");
    // const [isTranscribing, setIsTranscribing] = useState(false)

    // All the refs moved from index.jsx
    const socketRef = useRef(null);
    const audioContextRef = useRef(null);
    const processorRef = useRef(null);
    const sourceRef = useRef(null);
    const streamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const chunkCounterRef = useRef(0);
    const chunkStartTimeRef = useRef(0);
    const messageIdRef = useRef(null);
    const transcriptionStartedRef = useRef(false);
    const keepAliveIntervalRef = useRef(null);
    const recordingCounterRef = useRef(0);
    // What we have written to completeTranscript this recording, keyed by
    // message id. This used to be re-read from Firebase before EVERY appended
    // speech segment (~every 3 s), and the node grows all session, so the cost
    // was quadratic: 390 reads / 42.8 MB in 20 minutes on one live session,
    // 99% of it text this same client had just written. Recording is
    // single-client (the doctor's browser), so the local copy is authoritative;
    // Firebase is read once per recording to pick up anything already there.
    const completeTranscriptRef = useRef({ id: null, text: null });
    const meetmsgIDRef = useRef(null);
    const audioNotificationIntervalRef = useRef(null);
    const maxChunkDuration = 30000;

    // Add missing helper function
    const isTranscriptionActive = () => {
        const isSocketActive = socketRef.current?.readyState === WebSocket.OPEN;
        const isRecording = mediaRecorderRef.current?.state === 'recording';
        const hasStreams = streamRef.current?.getTracks().length > 0;
        const isScreenSharing = streamRef.current?.getTracks().some(track => track.kind === 'video' && track.readyState === 'live');
        const isMicOn = streamRef.current?.getTracks().some(track => track.kind === 'audio' && track.readyState === 'live');

        return isSocketActive || isRecording || hasStreams || isScreenSharing || isMicOn;
    };

    // Keep ref in sync so meetmsgID is always accessible even after Redux clears it
    useEffect(() => {
        if (meetmsgID) {
            meetmsgIDRef.current = meetmsgID;
        }
    }, [meetmsgID]);

    // Send FCM notification to all of the doctor's PHD tokens
    const sendScreenShareNotification = async (message) => {
        try {

            const phdTokens = doctorProfile?.notificationTokens?.PHD

            if (!phdTokens || typeof phdTokens !== 'object') {
                console.warn('[ScreenShare] No PHD tokens found — notification not sent. phdTokens:', phdTokens)
                return
            }

            const entries = Object.entries(phdTokens)

            const promises = entries.map(([key, token]) => {
                if (!token) {
                    console.warn('[ScreenShare] Skipping empty token for key:', key)
                    return Promise.resolve()
                }
                const payload = {
                    notificationToken: token,
                    data: {
                        ReminderType: 'screenShareWarning',
                        message,
                        chatID: currentSession?.conversationID || '',
                    },
                }
                console.log('[ScreenShare] Sending notification to token key:', key, 'payload:', payload)
                return customCloudMessage(JSON.stringify(payload))
                    .then(res => console.log('[ScreenShare] Notification sent for key:', key, 'response:', res))
                    .catch(e => console.warn('[ScreenShare] Failed for key:', key, 'error:', e))
            })

            await Promise.all(promises)
            console.log('[ScreenShare] All notifications dispatched')
        } catch (e) {
            console.warn('[ScreenShare] sendScreenShareNotification error:', e)
        }
    }

    const modelShow = () => {
        return new Promise((resolve) => {
            // Show modal with instructions
            Modal.info({
                title: "Meeting Start Instructions - Please Share Audio",
                icon: <span style={{ color: '#ff4d4f' }}></span>, // 🔴 Changed from default blue to red
                content: (
                    <div>
                        <p>To ensure proper transcription, please:</p>
                        <ul>
                            <li>Select the correct meeting tab when prompted</li>
                            <li>make sure <strong>"Share tab audio"</strong> is selected</li>
                            <li>Click on Allow </li>
                            <li>Open Meet Link and at Top Click on <strong>"Share this tab instead" Very Important</strong></li>
                            <li>After meeting end click  "Stop Transcription"</li>
                        </ul>
                        <p>Once you click OK, transcription will begin automatically.</p>
                    </div>
                ),
                okText: "Start Transcription",
                okButtonProps: {
                    style: { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f', color: '#fff' }, // 🔴 Red button
                },
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
                width: 520
            });
        });
    }
    // Move all transcription functions from index.jsx
    const startTranscription = async () => {
        // Check if already running
        if (isTranscriptionActive()) {
            console.log("Transcription resources already active, not starting again");
            // setIsTranscribing(true);
            return;
        }

        if (transcriptionStartedRef.current) {
            // Prevent duplicate transcription start
            console.log("Transcription already started for this session, skipping duplicate start.");
            return;
        }
        transcriptionStartedRef.current = true;

        try {
            const modelResponse = await modelShow();
            if (!modelResponse) {
                console.log("model is not selected");

                return false;
            }
            console.log("Starting transcription with Deepgram WebSocket API...");

            // Reset refs for a new recording session
            chunksRef.current = [];
            chunkCounterRef.current = 0;
            chunkStartTimeRef.current = Date.now();
            recordingCounterRef.current += 1; // Increment recording counter for unique file names
            messageIdRef.current = `transcript_${Date.now()}`;

            // Always reset the meetmsgID ref for a fresh session so no stale ID
            // from a previous session leaks into this one, then sync with the
            // current Redux value if it is already available.
            meetmsgIDRef.current = meetmsgID || null;

            // Create a new AudioContext
            const audioContext = new (window.AudioContext || window.webkit.AudioContext)({
                sampleRate: 48000,
                latencyHint: 'interactive'
            });
            audioContextRef.current = audioContext;

            // Create audio processing nodes
            const merger = audioContext.createChannelMerger(2);
            const destination = audioContext.createMediaStreamDestination();
            merger.connect(destination);

            // Stream containers
            let micStream = null;
            let tabStream = null;

            try {
                // Get microphone audio
                micStream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true
                    }
                });
                console.log("Microphone acquired:", micStream.getAudioTracks().length, "tracks");

                // Create processing chain for microphone
                const micSource = audioContext.createMediaStreamSource(micStream);
                const micGain = audioContext.createGain();
                micGain.gain.value = 1.0; // Full microphone volume
                const micPanner = audioContext.createStereoPanner();
                micPanner.pan.value = -0.3; // Slightly left

                // Connect microphone processing chain
                micSource.connect(micGain);
                micGain.connect(micPanner);
                micPanner.connect(merger);
                console.log("Microphone connected to audio processing chain");
            } catch (err) {
                console.warn("Could not access microphone:", err);
                notification.warning({
                    message: "Microphone Access Denied",
                    description: "Could not access your microphone. Please check permissions.",
                });
            }

            try {
                // Get tab audio (Google Meet)
                tabStream = await navigator.mediaDevices.getDisplayMedia({
                    video: {
                        displaySurface: "browser",
                        cursor: "always",
                        logicalSurface: true,
                        width: { ideal: 1920 },
                        height: { ideal: 1080 },
                        frameRate: { max: 15 }
                    },
                    audio: {
                        autoGainControl: false,
                        echoCancellation: false,
                        noiseSuppression: false,
                        channelCount: 2,
                        sampleRate: 48000
                    },
                    preferCurrentTab: true,
                    surfaceSwitching: "include",
                    selfBrowserSurface: "include"
                });

                // If user cancels screen sharing, tabStream will be undefined or have no tracks
                if (!tabStream || tabStream.getTracks().length === 0) {
                    // Stop microphone tracks if acquired
                    if (micStream && micStream.getTracks) {
                        micStream.getTracks().forEach(track => track.stop());
                    }

                    notification.warning({
                        message: "Screen Sharing Cancelled",
                        description: "You did not select a tab to share. Transcription will not start.",
                    });
                    // setIsTranscribing(false);

                    // Clean up audio context
                    if (audioContextRef.current) {
                        await audioContextRef.current.close();
                        audioContextRef.current = null;
                    }
                    return; // Exit early, do not run socket
                }

                if (tabStream.getAudioTracks().length > 0) {
                    console.log("Tab audio acquired:", tabStream.getAudioTracks().length, "tracks");

                    // Create processing chain for tab audio
                    const tabSource = audioContext.createMediaStreamSource(tabStream);
                    const tabGain = audioContext.createGain();
                    tabGain.gain.value = 0.8; // Slightly lower tab volume
                    const tabPanner = audioContext.createStereoPanner();
                    tabPanner.pan.value = 0.3; // Slightly right

                    // Connect tab audio processing chain
                    tabSource.connect(tabGain);
                    tabGain.connect(tabPanner);
                    tabPanner.connect(merger);
                    console.log("Tab audio connected to audio processing chain");

                    // ── Tab audio presence check ──────────────────────────────
                    // The analyser is connected only to tabSource (tab stream),
                    // so it measures tab audio exclusively — not mic or merged audio.
                    //
                    // Logic:
                    //   • Poll every 2 s for up to 10 s (first 10 s after start).
                    //   • If any signal detected → stop forever, no notifications.
                    //   • If no signal after 10 s → send notification immediately,
                    //     then repeat every 2 minutes until transcription stops.
                    const analyser = audioContext.createAnalyser()
                    analyser.fftSize = 256
                    tabSource.connect(analyser) // tab stream ONLY — not merged output

                    const silenceData = new Uint8Array(analyser.frequencyBinCount)
                    const SILENCE_THRESHOLD = 5    // avg bin value 0–255
                    const POLL_MS = 2000            // check every 2 s
                    const INITIAL_WINDOW_MS = 120000 // check for first 2mint s only
                    const NOTIFY_REPEAT_MS = 120000 // repeat notification every 2 min
                    let initialCheckedMs = 0

                    const silenceCheckInterval = setInterval(() => {
                        initialCheckedMs += POLL_MS

                        analyser.getByteFrequencyData(silenceData)
                        const rms = silenceData.reduce((sum, v) => sum + v, 0) / silenceData.length
                        console.log('[AudioMonitor] Tab stream rms:', rms.toFixed(2), '— elapsed:', initialCheckedMs / 1000, 's')

                        if (rms > SILENCE_THRESHOLD) {
                            // Audio is flowing from the tab — done, never notify
                            console.log('[AudioMonitor] Tab audio detected at', initialCheckedMs / 1000, 's — monitor stopped')
                            clearInterval(silenceCheckInterval)
                            streamRef.current.silenceCheckInterval = null
                            return
                        }

                        if (initialCheckedMs >= INITIAL_WINDOW_MS) {
                            // No audio in first 10 s — send first notification now
                            clearInterval(silenceCheckInterval)
                            streamRef.current.silenceCheckInterval = null

                            const msg = 'No audio is being received from the Google Meet tab. Please make sure click "Share this tab instead" in Google Meet and make sure "Share tab audio" is enabled.'
                            console.warn('[AudioMonitor] No tab audio in first 10 s — sending notification and starting 2-min repeat')
                            sendScreenShareNotification(msg)

                            // Repeat every 2 minutes until transcription stops
                            audioNotificationIntervalRef.current = setInterval(() => {
                                sendScreenShareNotification(msg)
                                console.warn('[AudioMonitor] Repeating screen share notification (2-min interval)')
                            }, NOTIFY_REPEAT_MS)
                        }
                    }, POLL_MS)

                    streamRef.current.silenceCheckInterval = silenceCheckInterval
                    // ─────────────────────────────────────────────────────────

                    // Store transcription state and meetmsgID in localStorage
                    if (currentSession?.conversationID) {
                        localStorage.setItem(`transcription_${currentSession.conversationID}`, 'true');
                        if (meetmsgID) {
                            localStorage.setItem(`meetmsgID_${currentSession.conversationID}`, meetmsgID);
                        }
                    }

                    // Disable video but keep track to maintain connection
                    tabStream.getVideoTracks().forEach(track => {
                        track.enabled = false;
                    });
                } else {
                    // No tab audio — check if wrong surface was selected
                    const videoTrack = tabStream.getVideoTracks()[0]
                    const surface = videoTrack?.getSettings()?.displaySurface
                    const msg = surface && surface !== 'browser'
                        ? `Screen sharing is set to "${surface}" instead of a browser tab. Please make sure the Google Meet tab and enable "Share tab audio" to capture patient voice.`
                        : 'No tab audio detected. Please re-share the Google Meet tab and make sure "Share tab audio" is enabled.'

                    console.warn('No audio track — starting notification interval:', msg)
                    sendScreenShareNotification(msg)
                    audioNotificationIntervalRef.current = setInterval(() => {
                        sendScreenShareNotification(msg)
                    }, 60000)
                }
            } catch (err) {
                // Doctor did not grant screen sharing permission — continue mic-only
                // and notify via FCM every minute until they share correctly.
                if (err && err.name === "NotAllowedError") {
                    console.log("Screen sharing permission denied — continuing mic-only, sending notifications")
                    const msg = 'Screen sharing was not enabled. Please allow screen sharing and select the Google Meet tab with "Share tab audio" enabled so patient voice can be captured.'
                    sendScreenShareNotification(msg)
                    audioNotificationIntervalRef.current = setInterval(() => {
                        sendScreenShareNotification(msg)
                    }, 60000)
                    // Do NOT return — let transcription continue with mic only
                } else {
                    // Some other error (e.g. NotReadableError, AbortError) — log only,
                    // do not show a toast. Transcription continues with mic only and
                    // the audio monitor will send a notification if no tab audio arrives.
                    console.warn('[ScreenShare] getDisplayMedia error (non-blocking):', err?.name, err?.message)
                }
            }

            // Store streams for cleanup
            streamRef.current = {
                getTracks: () => {
                    const tracks = [];
                    if (micStream) micStream.getTracks().forEach(track => tracks.push(track));
                    if (tabStream) tabStream.getTracks().forEach(track => tracks.push(track));
                    return tracks;
                }
            };

            // Create MediaRecorder from destination stream
            const options = {
                mimeType: 'audio/webm;codecs=opus',
                audioBitsPerSecond: 128000
            };

            const mediaRecorder = new MediaRecorder(destination.stream, options);
            mediaRecorderRef.current = mediaRecorder;
            console.log("MediaRecorder created with destination stream");

            // Create WebSocket connection to Deepgram
            const socket = new WebSocket('wss://api.deepgram.com/v1/listen?model=nova-3-medical&max_speakers=2&smart_format=true&punctuate=true&diarize=true&language=en-US', [
                'token',
                process.env.REACT_APP_DEEPGRAM_API_KEY
            ]);
            socketRef.current = socket;

            // Handle WebSocket open event
            socket.onopen = () => {
                console.log('Deepgram WebSocket connection opened');
                const socketId = Date.now(); // Generate unique ID for this socket
                // setActiveSocketId(socketId);
                socketRef.current = socket;
                // setIsTranscribing(true);

                // Store socket info in localStorage
                localStorage.setItem('activeSocketId', socketId);
                localStorage.setItem('transcriptionActive', 'true');

                // Start media recorder and send chunks to Deepgram
                mediaRecorder.addEventListener('dataavailable', (event) => {
                    if (event.data.size > 0) {
                        // Send audio data to Deepgram for live transcription
                        if (socket.readyState === WebSocket.OPEN) {
                            socket.send(event.data);
                        }
                        // Accumulate all chunks — single full audio uploaded on stop
                        chunksRef.current.push(event.data);
                    }
                });

                mediaRecorder.start(250); // Send chunks every 250ms for lower latency

                // Send keepalive signal every 5 s to prevent the socket from closing
                keepAliveIntervalRef.current = setInterval(() => {
                    if (socket.readyState === WebSocket.OPEN) {
                        socket.send(JSON.stringify({ type: 'KeepAlive' }));
                        console.log('Sent KeepAlive to Deepgram WebSocket');
                    }
                }, 5000);
            };

            // Store the session start time for calculating relative timestamps
            const sessionStartTime = Date.now();

            // Handle WebSocket messages (transcripts)
            socket.onmessage = async (message) => {
                try {
                    const received = JSON.parse(message.data);
                    console.log("received transcript data:", received);

                    const transcript = received.channel?.alternatives?.[0]?.transcript || '';

                    if (transcript.trim() !== '') {
                        console.log('Live transcript:', transcript);
                        setLiveTranscript(transcript);
                        // Get the current timestamp for unique identification
                        const transcriptTimestamp = Date.now();

                        // Only write to Firebase on final transcripts — no chunk/word/segment storage
                        if (received.is_final) {
                            const activeMsgID = meetmsgIDRef.current || meetmsgID;
                            if (!activeMsgID) {
                                console.warn('Transcript received but meetmsgID not yet available — skipping Firebase write');
                                return;
                            }

                            // Build speaker-labelled segments from word-level diarization
                            const words = received.channel?.alternatives?.[0]?.words || [];
                            const speakerSegments = [];
                            let currentSpeaker = null;
                            let currentText = '';

                            words.forEach(word => {
                                const speaker = word.speaker || 0;
                                if (currentSpeaker !== speaker && currentText) {
                                    speakerSegments.push({ speaker: `Speaker ${currentSpeaker}`, text: currentText.trim() });
                                    currentText = '';
                                }
                                currentSpeaker = speaker;
                                currentText += ` ${word.word}`;
                            });
                            if (currentText) {
                                speakerSegments.push({ speaker: `Speaker ${currentSpeaker}`, text: currentText.trim() });
                            }

                            // Format segments as "HH:MM:SS [Speaker X]: text"
                            const relativeTime = transcriptTimestamp - sessionStartTime;
                            const hh = Math.floor(relativeTime / 3600000).toString().padStart(2, '0');
                            const mm = Math.floor((relativeTime % 3600000) / 60000).toString().padStart(2, '0');
                            const ss = Math.floor((relativeTime % 60000) / 1000).toString().padStart(2, '0');
                            const formattedSegments = speakerSegments
                                .map(seg => `${hh}:${mm}:${ss} [${seg.speaker}]: ${seg.text}`)
                                .join('\n') + (speakerSegments.length ? '\n' : '')

                            try {
                                // Single write: append to completeTranscript only
                                const transcriptRef = ref(database, `/mentalHealthTranscript/${currentSession.conversationID}/${activeMsgID}`);
                                if (completeTranscriptRef.current.id !== activeMsgID) {
                                    // First segment of this recording: read the
                                    // completeTranscript LEAF once (not the whole
                                    // node, which also carries the chunk index).
                                    const snapshot = await get(child(transcriptRef, 'completeTranscript'));
                                    completeTranscriptRef.current = { id: activeMsgID, text: snapshot.val() || '' };
                                }
                                const existing = completeTranscriptRef.current.text;
                                const nextTranscript = existing + formattedSegments;
                                await update(transcriptRef, {
                                    completeTranscript: nextTranscript,
                                    lastUpdated: transcriptTimestamp,
                                    callType: 'meeting',
                                });
                                // Advance only after the write lands, so a failed
                                // write retries the same text next segment instead
                                // of silently dropping it from the transcript.
                                completeTranscriptRef.current = { id: activeMsgID, text: nextTranscript };
                            } catch (error) {
                                console.error('Error updating completeTranscript in Firebase:', error);
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error handling Deepgram transcript:', error);
                }
            };

            // Handle WebSocket errors and close events
            socket.onerror = (error) => {
                console.error('Deepgram WebSocket error:', error);
            };

            socket.onclose = async (event) => {
                console.log('Deepgram WebSocket closed:', event);

                try {
                    // Fetch the complete transcript after socket is closed
                    const transcriptRef = ref(database, `/mentalHealthTranscript/${currentSession.conversationID}/${meetmsgIDRef.current || meetmsgID}`);
                    const snapshot = await get(transcriptRef);
                    const currentData = snapshot.val() || {};
                    const completeTranscript = currentData.completeTranscript || '';

                    if (completeTranscript) {
                        // Send the transcript to the AI processing API
                        console.log("Sending transcript to AI processing API...");
                        const transcriptObj = {
                            chatID: currentSession.conversationID,
                            messageID: meetmsgIDRef.current || meetmsgID,
                            transcript: completeTranscript,
                            sessionID: currentSession?.currentSessionID || null,
                            chatType: "mental_health"
                        };
                        const apiResponse = await soapNoteAIMeeting(transcriptObj);

                        try {
                            // Fetch the complete transcript after socket is closed
                            const transcriptRef = ref(database, `/mentalHealthTranscript/${currentSession.conversationID}/${meetmsgIDRef.current || meetmsgID}`);
                            const snapshot = await get(transcriptRef);
                            const currentData = snapshot.val() || {};
                            const completeTranscript = currentData.completeTranscript || '';

                            if (completeTranscript) {
                                // Send the transcript to the AI processing API
                                console.log("Sending transcript to AI processing API...");
                                const transcriptObj = {
                                    chatID: currentSession.conversationID,
                                    messageID: meetmsgIDRef.current || meetmsgID,
                                    transcript: completeTranscript,
                                    sessionID: currentSession?.currentSessionID || null,
                                    chatType: "mental_health"
                                };
                                const apiResponse = await soapNoteAIMeeting(transcriptObj);
                                console.log("AI processing API response:", apiResponse);

                                await update(transcriptRef, {
                                    aiProcessed: true,
                                    callType: "meeting",
                                    aiProcessingTimestamp: Date.now(),
                                    summary: apiResponse?.summary || "",
                                    aiTranscript: apiResponse?.transcript || completeTranscript
                                });

                                if (currentSession?.conversationID) {
                                    localStorage.removeItem(`transcription_${currentSession.conversationID}`);
                                    localStorage.removeItem(`meetmsgID_${currentSession.conversationID}`);
                                }
                            }
                        } catch (error) {
                            console.error("Error sending transcript to AI processing API:", error);
                        }
                        console.log("AI processing API response:", apiResponse);

                        if (currentSession?.conversationID) {
                            localStorage.removeItem(`transcription_${currentSession.conversationID}`);
                            localStorage.removeItem(`meetmsgID_${currentSession.conversationID}`);
                        }
                    }
                } catch (error) {
                    console.error("Error sending transcript to AI processing API:", error);
                }
            };

            // Set up references for the media recorder
            streamRef.current.mediaRecorder = mediaRecorder;
            streamRef.current.messageId = messageIdRef.current;

            // Set up a chunk timer to request data periodically
            const chunkTimer = setInterval(() => {
                if (mediaRecorder && mediaRecorder.state === 'recording') {
                    mediaRecorder.requestData();
                }
            }, 20000);

            streamRef.current.chunkTimer = chunkTimer;

            // Notify user of success
            notification.success({
                message: "Transcription Started",
                description: tabStream && tabStream.getAudioTracks().length > 0
                    ? "Capturing both your microphone and the Google Meet audio for transcription."
                    : "Only capturing your microphone for transcription. To include Google Meet audio, share the tab.",
                duration: 5
            });

        } catch (err) {
            console.error("🎙️ Audio transcription setup error:", err);
            // Clear localStorage on error
            if (currentSession?.conversationID) {
                localStorage.removeItem(`transcription_${currentSession.conversationID}`);
                localStorage.removeItem(`meetmsgID_${currentSession.conversationID}`);
            }
            // setIsTranscribing(false);
            notification.error({
                message: "Transcription Error",
                description: "Failed to start transcription: " + err.message,
            });
        }
    };

    const stopTranscription = async () => {
        // Capture meetmsgID BEFORE clearing Redux or localStorage so all Firebase
        // writes go to the correct node even after state is wiped.
        const activeMeetmsgID =
            meetmsgIDRef.current ||
            meetmsgID ||
            localStorage.getItem(`meetmsgID_${currentSession?.conversationID}`);

        transcriptionStartedRef.current = false;
        dispatch(setTranscriptionState(false));
        dispatch(setMeetMessageId(null));
        dispatch(setStopKey(null));
        dispatch(setForceStop(false));
        dispatch(setStopTranscription(false));
        dispatch(setMeetButtonClicked(false));

        try {
            console.log("Stopping transcription... activeMeetmsgID:", activeMeetmsgID);

            // Clear Redux state and localStorage AFTER we've captured the ID
            if (currentSession?.conversationID) {
                localStorage.removeItem(`transcription_${currentSession.conversationID}`);
                localStorage.removeItem(`meetmsgID_${currentSession.conversationID}`);
                dispatch(setMeetMessageId(null));
            }
            // Also clear the ref now that we've saved the value locally
            meetmsgIDRef.current = null;

            // Clear keepalive interval
            if (keepAliveIntervalRef.current) {
                clearInterval(keepAliveIntervalRef.current);
                keepAliveIntervalRef.current = null;
            }

            // Clear screen-share notification interval
            if (audioNotificationIntervalRef.current) {
                clearInterval(audioNotificationIntervalRef.current);
                audioNotificationIntervalRef.current = null;
            }

            // Clear silence-check interval
            if (streamRef.current?.silenceCheckInterval) {
                clearInterval(streamRef.current.silenceCheckInterval);
                streamRef.current.silenceCheckInterval = null;
            }

            // Stop media recorder and wait for all final data before uploading
            if (streamRef.current && streamRef.current.mediaRecorder) {
                if (streamRef.current.mediaRecorder.state === 'recording') {
                    console.log("Stopping media recorder and collecting final audio...");
                    await new Promise(resolve => {
                        streamRef.current.mediaRecorder.onstop = resolve;
                        streamRef.current.mediaRecorder.requestData();
                        setTimeout(() => {
                            if (streamRef.current?.mediaRecorder?.state !== 'inactive') {
                                streamRef.current.mediaRecorder.stop();
                            }
                        }, 500);
                    });
                }
                if (streamRef.current.chunkTimer) {
                    clearInterval(streamRef.current.chunkTimer);
                }
            }

            // Upload the complete audio recording — always pass activeMeetmsgID so
            // it is never written to firstMessageID by mistake.
            if (chunksRef.current.length > 0) {
                storeFullAudioToFirebase(chunksRef.current, messageIdRef.current, recordingCounterRef.current, activeMeetmsgID)
                    .catch(error => console.error('Error storing full audio:', error));
            }

            // Update Firebase recording status
            if (currentSession && activeMeetmsgID) {
                await update(
                    ref(database, `/mentalHealthTranscript/${currentSession.conversationID}/${activeMeetmsgID}`),
                    {
                        endTimestamp: Date.now(),
                        status: 'completed'
                    }
                ).catch(err => console.error("Error updating transcript status:", err));
            }

            // Close WebSocket
            if (socketRef.current) {
                socketRef.current.close();
                socketRef.current = null;
            }

            // Stop all media tracks
            if (streamRef.current) {
                const tracks = streamRef.current.getTracks();
                tracks.forEach(track => {
                    track.stop();
                    if (track.kind === 'video' && track.label.includes('screen')) {
                        console.log('Stopping screen sharing track:', track.label);
                    }
                });
            }

            // Cleanup audio processing
            if (processorRef.current) {
                processorRef.current.disconnect();
                sourceRef.current?.disconnect();
                processorRef.current = null;
                sourceRef.current = null;
            }

            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                await audioContextRef.current.close();
                audioContextRef.current = null;
            }

            // Reset all refs
            streamRef.current = null;
            mediaRecorderRef.current = null;
            chunksRef.current = [];
            transcriptionStartedRef.current = false;
            setLiveTranscript("");
            // Notify user
            notification.info({
                message: "Transcription Stopped",
                description: "Transcription has been stopped and all sharing has ended.",
                duration: 3
            });

            console.log("Transcription stopped and audio data saved");
        } catch (error) {
            console.error("Error stopping transcription:", error);
            // Even if there's an error, ensure Redux state is reset
            dispatch(setTranscriptionState(false));
            dispatch(setMeetMessageId(null));
        }
    };

    const storeFullAudioToFirebase = async (audioChunks, messageId, recordingNumber, targetMeetmsgID) => {
        try {
            if (!audioChunks?.length) return;

            // Always use the explicitly-passed targetMeetmsgID so we never fall
            // back to firstMessageID after Redux state has been cleared.
            const targetID = targetMeetmsgID || meetmsgIDRef.current || meetmsgID;
            if (!targetID) {
                console.error('storeFullAudioToFirebase: no meetmsgID available, aborting upload');
                return;
            }

            const audioBlob = new Blob(audioChunks, { type: 'audio/webm;codecs=opus' });

            if (audioBlob.size < 1000) {
                console.log('Skipping upload: audio recording is too small');
                return;
            }

            const formattedChunkNum = String(recordingNumber).padStart(2, '0');
            const storagePath = `mentalHealthTranscript/${currentSession.conversationID}/${targetID}/chunk_${formattedChunkNum}.webm`;
            const audioRef = storageRef(storage, storagePath);

            try {
                await uploadBytesResumable(audioRef, audioBlob);
                const downloadURL = await getDownloadURL(audioRef);

                await update(ref(database, `/mentalHealthTranscript/${currentSession.conversationID}/${targetID}/chunks/${formattedChunkNum}`), {
                    callType: "meeting",
                    url: downloadURL,
                    timestamp: Date.now(),
                    size: audioBlob.size,
                    duration: audioBlob.size / 2000,
                    order: recordingNumber,
                    status: 'processed'
                });

                console.log('Full audio recording uploaded to Firebase:', downloadURL);
            } catch (error) {
                console.error('Full audio upload error:', error);
                await update(ref(database, `/mentalHealthTranscript/${currentSession.conversationID}/${targetID}/chunks/${formattedChunkNum}`), {
                    timestamp: Date.now(),
                    callType: "meeting",
                    error: error.message,
                    status: 'failed'
                });
            }
        } catch (error) {
            console.error("Firebase storage error:", error);
        }
    };

    // Cleanup effect
    useEffect(() => {
        return () => {
            if (isTranscribing) {
                stopTranscription();
            }
        };
    }, []);

    // Check transcription state on mount
    useEffect(() => {
        const transcriptionActive = localStorage.getItem(`transcription_${currentSession?.conversationID}`);
        const storedMeetMsgID = localStorage.getItem(`meetmsgID_${currentSession?.conversationID}`);

        if (transcriptionActive === 'true' && currentSession && !isTranscriptionActive()) {
            if (storedMeetMsgID) {
                dispatch(setMeetMessageId(storedMeetMsgID));
            }
            dispatch(setTranscriptionState(true));

            startTranscription();
        }
    }, [currentSession]);

    // // Add new useEffect for handling page reload persistence
    // useEffect(() => {
    //     const transcriptionActive = localStorage.getItem(`transcription_${currentSession?.conversationID}`);
    //     const storedMeetMsgID = localStorage.getItem(`meetmsgID_${currentSession?.conversationID}`);

    //     if (transcriptionActive === 'true') {
    //         dispatch(setTranscriptionState(true));
    //         if (storedMeetMsgID) {
    //             dispatch(setMeetMessageId(storedMeetMsgID));
    //         }
    //     }
    // }, []);

    // Add new useEffect to handle auto-start on meetButtonClicked
    useEffect(() => {
        const handleAutoStart = async () => {
            if (meetButtonClicked && !isTranscribing && currentSession) {
                console.log("Auto-starting transcription due to meet button click");

                // On every (re-)start, ensure the ref reflects the new meetmsgID.
                // Sync it now so startTranscription has it immediately — even
                // before the useEffect([meetmsgID]) has a chance to run.
                if (meetmsgID) {
                    meetmsgIDRef.current = meetmsgID;
                }

                // Wait for meetmsgID — increased to 10 attempts (5 seconds total).
                // We do NOT start without a valid meetmsgID to avoid writing to
                // the wrong Firebase node on a restarted session.
                let attempts = 0;
                const maxAttempts = 10;

                while (!meetmsgIDRef.current && attempts < maxAttempts) {
                    console.log(`Waiting for meetmsgID... attempt ${attempts + 1}/${maxAttempts}`);
                    await new Promise(resolve => setTimeout(resolve, 500));
                    attempts++;
                }

                if (meetmsgIDRef.current) {
                    console.log("meetmsgID available:", meetmsgIDRef.current);
                    await startTranscription();
                    dispatch(setTranscriptionState(true));
                } else {
                    console.error("meetmsgID not available after waiting — transcription blocked to prevent wrong node write");
                    notification.error({
                        message: "Cannot Start Transcription",
                        description: "Meet message ID was not received. Please try again.",
                    });
                    dispatch(setMeetButtonClicked(false));
                }
            }
        };

        handleAutoStart();
    }, [meetButtonClicked, meetmsgID]); // meetmsgID re-triggers the effect when the new ID arrives


    // // Watch for changes in isTranscribing and handle transcription accordingly
    // useEffect(() => {
    //     const handleTranscription = async () => {
    //         if (isTranscribing && currentSession) {
    //             alert("Called in useEffect handleTranscription");

    //             await startTranscription();
    //         }
    //     };

    //     handleTranscription();
    // }, [isTranscribing, currentSession]);

    // Log effect to monitor isTranscribing state
    useEffect(() => {
        if (isTranscribing) {
            console.log("Transcription is active. You can stop it by clicking any button.");
        }
    }, [isTranscribing]);



    // Add new useEffect to watch for stop signal
    useEffect(() => {
        if (shouldStopTranscription && isTranscribing) {
            stopTranscription();

            dispatch(setStopTranscription(false)); // Reset the stop signal
        }
    }, [shouldStopTranscription]);

    // // Modify existing useEffect to handle forceStop
    // useEffect(() => {
    //     if (forceStop || !isTranscribing) {
    //         stopTranscription();
    //         dispatch(setForceStop(false));
    //     }
    // }, [forceStop, isTranscribing]);

    // Conditionally render the component based on isTranscribing
    if (!isTranscribing) return null;

    return (
        <>
            <div className={styles.transcriptionManagerGlobal}>
                <Button
                    className={styles.transcriptionButton}
                    disabled={!currentSession || currentSession?.inbox || currentSession?.isArchive}
                    onClick={async () => {
                        if (!isTranscribing) {
                            await startTranscription();
                            dispatch(setTranscriptionState(true));
                        } else {
                            await stopTranscription();
                            dispatch(setTranscriptionState(false));
                        }
                    }}
                >
                    <SiOkta
                        color={isTranscribing ? '#DF7467' : '#DF7467'}
                        size="20px"
                    />
                    <span className={styles.buttonLabel}>
                        {isTranscribing ? 'Stop' : 'Start'} Transcript
                    </span>
                </Button>
                <p>Transcription is active for : {currentSession?.conversationID}</p>
            </div>
            {/* Subtitle Portal UI for live transcript, rendered as portal to document.body */}
            {ReactDOM.createPortal(
                <div className={styles.subtitlePortal}>
                    <div className={styles.subtitleText}>{liveTranscript}</div>
                </div>,
                document.body
            )}
        </>
    );
};

export default TranscriptionManager;
